import './AnnouncementToast.css';

import React, { useEffect, useRef } from 'react';

import { useAnnouncements } from '../../../contexts/AnnouncementsContext';
import { MegaphoneIcon } from '../../../pages/TryoutIcons';

const DismissIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const AnnouncementToast: React.FC = () => {
  const { announcements, undismissed, dismiss } = useAnnouncements();
  const top = undismissed[0] ?? null;

  // Vibrate only when a genuinely new announcement arrives, not when dismissal
  // shifts which one is shown as top.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);
  // useEffect is appropriate here: triggering a device vibration side effect.
  useEffect(() => {
    if (!initializedRef.current) {
      // Seed seen set on mount so existing announcements don't buzz.
      initializedRef.current = true;
      announcements.forEach((a) => seenIdsRef.current.add(a.id));
      return;
    }
    const hasNew = announcements.some((a) => !seenIdsRef.current.has(a.id));
    if (!hasNew) return;
    announcements.forEach((a) => seenIdsRef.current.add(a.id));
    navigator.vibrate?.([100, 70, 100]);
  }, [announcements]);

  return (
    <div
      className={`announcement-toast${top ? ' announcement-toast--visible' : ''}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {top && (
        <div className="announcement-toast__inner">
          <div className="announcement-toast__icon-wrap">
            <MegaphoneIcon size={16} />
          </div>
          <div className="announcement-toast__content">
            <p className="announcement-toast__text">{top.text}</p>
            <div className="announcement-toast__meta">
              <span className="announcement-toast__author">{top.createdByName}</span>
              <span className="announcement-toast__meta-sep" aria-hidden>
                ·
              </span>
              <span className="announcement-toast__time">
                {top.createdAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
          </div>
          {undismissed.length > 1 && (
            <span className="announcement-toast__more-badge">+{undismissed.length - 1}</span>
          )}
          <button
            type="button"
            className="announcement-toast__dismiss"
            onClick={() => dismiss(top.id)}
            aria-label="Dismiss announcement"
          >
            <DismissIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default AnnouncementToast;
